<template>
  <block-item-card
    :min-height="300"
    :title="title || $t('ALM.Section.Profitability.Title')"
    :title-info="$t('ALM.Section.Profitability.TitleInfo')"
    :title-icon="titleIcon"
    :title-icon-size="titleIconSize"
    :style="dashboardView ? 'border:none;': ''"
    :override-title-size="dashboardView"
    :title-size="12"
  >
    <v-row class="ma-0">
      <!-- Left Section - Donut Chart -->
      <v-col
        v-if="!dashboardView"
        class="ma-0 pa-0 py-5 pt-6"
        cols="12"
        md="2"
      >
        <div class="text-center">
          <!-- Donut Chart -->
          <v-lazy
            :options="{threshold: .5}"
            transition="fade-transition"
          >
            <base-e-chart-pie
              :tooltip-prefix="currencySymbol"
              :chart-data-options="profitabilityPieData"
              :chart-height="190"
              :tooltip-show="true"
              chart-animation-easing="elasticOut"
              tooltip-confine
              :label-line-show="false"
              :label-show="false"
              :legend-show="false"
              :default-colors="pieColors"
            />
          </v-lazy>

          <!-- Custom Toggle Switch -->
          <div
            v-if="false"
            class="mt-4 d-flex justify-center"
          >
            <div class="profitability-toggle-switch">
              <span
                :class="['toggle-label']"
                @click="selectedPeriod = '3m'"
              >
                {{ $t('ALM.Section.Profitability.ThreeMonths') }}
              </span>
              <div
                class="toggle-track"
                @click="togglePeriod"
              >
                <div
                  :class="['toggle-thumb', {'active': selectedPeriod === '12m'}]"
                />
              </div>
              <span
                :class="['toggle-label']"
                @click="selectedPeriod = '12m'"
              >
                {{ $t('ALM.Section.Profitability.TwelveMonths') }}
              </span>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Right Section - Data Table -->
      <v-col
        :class="dashboardView ? 'ma-0 pa-0 py-5 px-4' : 'ma-0 pa-0 py-5 pr-4'"
        cols="12"
        :md="dashboardView ? 12 : 10"
      >
        <v-card
          elevation="0"
        >
          <v-data-table
            :headers="tableHeaders"
            :items="bankActivities"
            :items-per-page="-1"
            hide-default-footer
            disable-pagination
            disable-sort
            class="no-row-borders"
          >
            <!-- Bank Activity (keep separate, no background style here) -->
            <template #item.bankActivity="{item, index}">
              <div
                class="d-flex align-center alm-cell"
                :class="{'last-row': isLastItem(index)}"
                :style="item.style"
              >
                <v-avatar
                  :color="item.color"
                  size="12"
                  class="mr-3"
                />
                <span class="synthesis-text text-size-14 synthesis-brand-dark-blue--text">
                  {{ item.name }}
                </span>
              </div>
            </template>

            <!-- 3m (left segment) -->
            <template #item.threeMonths="{item, index}">
              <div
                class="alm-cell group-left"
                :class="{'last-row': isLastItem(index)}"
                :style="item.style"
              >
                <span
                  class="synthesis-text text-size-14"
                  :class="{'neg': item.threeMonthsRaw < 0}"
                  style="text-align:right;min-width:60px;"
                >
                  {{ item.threeMonths }}
                </span>
              </div>
            </template>

            <!-- 12m (middle segment, only when visible) -->
            <template #item.twelveMonths="{item, index}">
              <div
                class="alm-cell group-middle"
                :class="{'last-row': isLastItem(index)}"
                :style="item.style"
              >
                <span
                  :class="{'neg': item.twelveMonthsRaw < 0}"
                  class="synthesis-text text-size-14"
                  style="width:100% !important;margin-right:10px !important;"
                >
                  <div style="text-align:right !important;display:inline-block;width:60px !important;">
                    {{ item.twelveMonths }}
                  </div>
                </span>
              </div>
            </template>

            <!-- Yield (right segment, becomes right even if 12m is hidden) -->
            <template #item.yield="{item, index}">
              <div
                class="alm-cell"
                :class="[
                  selectedPeriod === '12m' ? 'group-right' : 'group-right--compact',
                  {'last-row': isLastItem(index)}
                ]"
                :style="item.style"
              >
                <span
                  class="synthesis-text text-size-14 synthesis-brand-dark-blue--text"
                  style="width: 100%;"
                >
                  {{ item.yield }}
                </span>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import BaseEChartPie from '@/theme/default/components/charts/echarts/BaseEChartPie'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'

export default {
  name      : 'Profitability',
  components: {
    BlockItemCard,
    BaseEChartPie
  },
  mixins: [chartsColorMixin],
  props : {
    dashboardView: {
      type   : Boolean,
      default: false
    },
    title: {
      type   : String,
      default: ''
    },
    titleIcon: {
      type   : String,
      default: ''
    },
    titleIconSize: {
      type   : [String, Number],
      default: 10
    }
  },
  dataStore: {
    ALMSummary: 'ALM.ALMSummary.Data'
  },
  data () {
    return {
      selectedPeriod: '12m',
      minBlockHeight: 350
    }
  },
  computed: {
    almSummary () {
      return this.ALMSummary || {}
    },

    tableWidth () {
      return '25%'
    },

    tableHeaders () {
      const headers = [
        {
          text    : this.$t('ALM.Section.Profitability.BankActivity'),
          value   : 'bankActivity',
          sortable: false,
          width   : this.tableWidth
        },
        {
          text    : this.$t('ALM.Section.Profitability.ThreeMonths'),
          value   : 'threeMonths',
          sortable: false,
          width   : this.tableWidth
        },
        {
          text    : this.$t('ALM.Section.Profitability.TwelveMonths'),
          value   : 'twelveMonths',
          sortable: false,
          width   : this.tableWidth,
          align   : 'center'
        },
        {
          text    : this.$t('ALM.Section.Profitability.Yield'),
          value   : 'yield',
          sortable: false,
          width   : this.tableWidth,
          align   : 'end'
        }
      ]

      return headers
    },

    profitabilityPieData () {
      return this.almSummary?.profitabilityPieData || []
    },

    currencySymbol () {
      return this.almSummary?.currencySymbol || '$'
    },

    bankActivities () {
      return this.almSummary?.profitabilityBankActivities || []
    },

    pieColors () {
      return ['#4CAF50', '#1976D2', '#FF9800']
    }
  },

  watch: {
    selectedPeriod () {
      this.updateResourcePeriod()
    }
  },
  methods: {
    togglePeriod () {
      this.selectedPeriod = this.selectedPeriod === '3m' ? '12m' : '3m'
      this.updateResourcePeriod()
    },

    updateResourcePeriod () {
      if (this.almSummary) {
        this.almSummary.selectedPeriod = this.selectedPeriod
      }
    },

    isLastItem (index) {
      return index === this.bankActivities.length - 1
    }
  }
}
</script>

<style scoped>
.profitability-toggle-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-label {
  font-size: 10px;
  font-weight: 400;
  color: #666;
  transition: color 0.3s ease;
  cursor: pointer;
  user-select: none;
}

.toggle-track {
  position: relative;
  width: 40px;
  min-width: 40px;
  height: 20px;
  background-color: #1976D2;
  border-radius: 100px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-thumb.active {
  transform: translateX(20px);
}

::v-deep .no-row-borders .v-data-table__wrapper tbody tr > td,
::v-deep .no-row-borders .v-data-table__wrapper tbody tr > th {
  border-bottom: 0 !important;
}

::v-deep .no-row-borders .v-data-table__wrapper tbody tr:hover,
::v-deep .no-row-borders .v-data-table__wrapper tbody tr:hover > td {
  background-color: transparent !important;
  box-shadow: none !important;
}
::v-deep .no-row-borders .v-data-table-header th {
  border-bottom: 0 !important;
  height: 20px;
  span {
    padding-left: 20px !important;
    font-size: 12px;
    font-weight: 400;
    color: #041F30;
  }
}

.alm-cell {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding: 0 20px;
  border-radius: 4px;
}

.group-left {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-left: 10px !important;
}
.group-middle {
  border-radius: 0;
  padding-left: 40px;
}
.group-right {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.group-right--compact {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.group-left { margin-left: 5px; }
.group-left   { border-right: none !important; }
.group-middle { border-left: none !important; border-right: none !important; }
.group-right,
.group-right--compact { border-left: none !important; }

/* Last row styling */
.last-row {
  font-weight: 700 !important;
}

.neg {
  color: var(--v-synthesis-ui-red-02-base) !important;
}

::v-deep .no-row-borders .v-data-table__wrapper table {
  border-collapse: separate;
  border-spacing: 0;
}

::v-deep .no-row-borders .v-data-table__wrapper tbody > tr > td {
  padding: 0 !important;
}

::v-deep .no-row-borders .v-data-table__wrapper td.text-start,
::v-deep .no-row-borders .v-data-table__wrapper th.text-start {
  padding-inline-start: 0 !important;
  padding-inline-end: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

::v-deep .v-data-table-header th:nth-child(4) {
  span {
    padding-right: 10px !important;
  }
  }
</style>
